import { createSlice } from '@reduxjs/toolkit';

const LOCAL_STORAGE_KEY = 'cartState';

const loadFromLocalStorage = () => {
  try {
    const serializedState = localStorage.getItem(LOCAL_STORAGE_KEY);
    return serializedState ? JSON.parse(serializedState) : undefined;
  } catch (error) {
    console.error('Could not load cart state from localStorage:', error);
    return undefined;
  }
};

const saveToLocalStorage = (state) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem(LOCAL_STORAGE_KEY, serializedState);
  } catch (error) {
    console.error('Could not save cart state to localStorage:', error);
  }
};

const initialState = {
  items: [], // Array of cart items
  totalQuantity: 0, // Total items in the cart
  totalProfit: 0, // Total profit across all items
  totalSubtotal: 0, // Total subtotal of all items
  totalMargin: 0, // Average margin of the cart
  storeID: null,
};

const cartSlice = createSlice({
  name: 'cart',
  initialState: loadFromLocalStorage() || initialState,
  reducers: {
    // Add a product to the cart
    addItem(state, action) {
      const newItem = action.payload;
      const existingItem = state.items.find((item) => item.name === newItem.name);

      if (existingItem) {
        // Update existing item
        const potentialQuantity = existingItem.quantity + newItem.quantity;

        // Ensure quantity does not exceed stock
        if (potentialQuantity > existingItem.number_of_stock) {
          const addableQuantity = existingItem.number_of_stock - existingItem.quantity;
          existingItem.quantity = existingItem.number_of_stock;
          existingItem.subtotal += addableQuantity * existingItem.price;
          existingItem.profit += addableQuantity * (existingItem.price - existingItem.cost_per_item);
        } else {
          existingItem.quantity = potentialQuantity;
          existingItem.subtotal += newItem.price * newItem.quantity;
          existingItem.profit += (newItem.price - newItem.cost_per_item) * newItem.quantity;
        }

        existingItem.discount =
          parseInt(existingItem.sale_price, 10) > 0
            ? (parseInt(existingItem.regular_price, 10) - parseInt(existingItem.sale_price, 10)) * existingItem.quantity
            : 0;
        existingItem.margin = existingItem.subtotal
          ? ((existingItem.profit / existingItem.subtotal) * 100).toFixed(2)
          : '0';
      } else {
        // Add new item
        state.items.push({
          ...newItem,
          quantity: Math.min(newItem.quantity, newItem.number_of_stock),
          subtotal: newItem.price * Math.min(newItem.quantity, newItem.number_of_stock),
          profit: (newItem.price - newItem.cost_per_item) * Math.min(newItem.quantity, newItem.number_of_stock),
          margin: (((newItem.price - newItem.cost_per_item) / newItem.price) * 100).toFixed(2),
          discount:
            parseInt(newItem.sale_price, 10) > 0
              ? (parseInt(newItem.regular_price, 10) - parseInt(newItem.sale_price, 10)) * newItem.quantity
              : 0,
        });
      }

      // Recalculate total values using reduce
      state.totalQuantity = state.items.reduce((sum, item) => sum + item.quantity, 0);
      state.totalSubtotal = state.items.reduce((sum, item) => sum + item.subtotal, 0);
      state.totalProfit = state.items.reduce((sum, item) => sum + item.profit, 0);
      state.totalDiscount = state.items.reduce((sum, item) => sum + item.discount, 0);
      state.totalMargin = state.totalSubtotal ? ((state.totalProfit / state.totalSubtotal) * 100).toFixed(2) : '0';
      state.storeID = newItem.store;
      saveToLocalStorage(state);
    },

    // Update quantity of an item
    updateQuantity(state, action) {
      const { name, quantity } = action.payload; // `quantity` will be -1 or +1
      const existingItemIndex = state.items.findIndex((item) => item.name === name);

      if (existingItemIndex !== -1) {
        const existingItem = state.items[existingItemIndex];

        // Adjust the item's quantity
        existingItem.quantity += quantity;

        // Ensure quantity doesn't exceed stock
        if (existingItem.quantity > existingItem.number_of_stock) {
          existingItem.quantity = existingItem.number_of_stock;
        }

        // Remove item if quantity drops to 0 or less
        if (existingItem.quantity <= 0) {
          state.items.splice(existingItemIndex, 1);
        } else {
          // Recalculate item-specific values if not removed
          existingItem.subtotal = existingItem.price * existingItem.quantity;
          existingItem.profit = (existingItem.price - existingItem.cost_per_item) * existingItem.quantity;
          existingItem.margin =
            existingItem.subtotal > 0 ? ((existingItem.profit / existingItem.subtotal) * 100).toFixed(2) : '0';
          existingItem.discount =
            parseInt(existingItem.sale_price, 10) > 0
              ? (parseInt(existingItem.regular_price, 10) - parseInt(existingItem.sale_price, 10)) *
                existingItem.quantity
              : 0;
        }

        // Recalculate total values
        state.totalQuantity = state.items.reduce((sum, item) => sum + item.quantity, 0);
        state.totalSubtotal = state.items.reduce((sum, item) => sum + item.subtotal, 0);
        state.totalProfit = state.items.reduce((sum, item) => sum + item.profit, 0);
        state.totalMargin =
          state.totalSubtotal > 0 ? ((state.totalProfit / state.totalSubtotal) * 100).toFixed(2) : '0';
        state.totalDiscount = state.items.reduce((sum, item) => sum + item.discount, 0);

        // Save updated state to local storage
        saveToLocalStorage(state);
      }
    },

    // Clear the cart
    clearCart(state) {
      saveToLocalStorage(initialState);
      state = initialState;
      console.log('🚀 ~ clearCart ~ state:', state);
      return initialState;
    },
  },
});

export const { addItem, removeItem, updateQuantity, clearCart } = cartSlice.actions;

export default cartSlice.reducer;
